<template>
  <div ref="Gallery" class="Gallery sf-gallery">
    <div class="sf-gallery__stage" :style="{maxHeight: (imageHeight>620?620:imageHeight)+'px'}">
      <div ref="glide" class="glide">
        <div class="glide__track" data-glide-el="track">
          <ul class="glide__slides">
            <li v-for="(picture, index) in images" :key="'slide-' + index" class="glide__slide"
              @mouseover="startZoom(picture)" @mousemove="moveZoom($event, index)" @mouseout="removeZoom(index)">
              <slot name="big-image" v-bind="{
                enableZoom,
                picture,
                index,
                imageWidth,
                imageHeight,
                imageTag,
                nuxtImgConfig,
              }">
                <SfImage ref="sfGalleryBigImage" class="sf-gallery__big-image"
                  :class="{ 'sf-gallery__big-image--has-zoom': enableZoom }" :src="picture.desktop.url"
                  :alt="picture.alt" :placeholder="picture.placeholder" :width="imageWidth" :height="imageHeight"
                  :image-tag="imageTag" :nuxt-img-config="nuxtImgConfig"
                  @click="$emit('click:stage', { picture, index })" />
              </slot>
            </li>
          </ul>
        </div>
      </div>
      <transition name="sf-fade">
        <div ref="outSide" :class="{
          'display-none':
            !outsideZoom || !isZoomStarted || (!outsideZoom && !enableZoom),
        }" :style="{ width: `${imageWidth}px`, height: `${imageHeight}px` }">
          {{ outsideZoom }}
          <slot name="outside-zoom" v-bind="{
            definedPicture,
            imageWidth,
            imageHeight,
            imageTag,
            nuxtImgConfig,
          }">
            <SfImage ref="imgZoom" class="sf-gallery__zoom" :src="definedPicture.url" :width="imageWidth"
              :height="imageHeight" :lazy="false" :alt="definedPicture.alt" :placeholder="definedPicture.placeholder"
              :image-tag="imageTag" :nuxt-img-config="nuxtImgConfig" />
          </slot>
        </div>
      </transition>
    </div>
    <div class="gallery__thumbsControll">
      <div class="sf-gallery__thumbs">
        <slot name="thumbs" v-bind="{ images, active: activeIndex, go }">
          <SfButton v-for="(image, index) in images" :key="'img-' + index" class="sf-button--pure sf-gallery__item"
            :class="{ 'sf-gallery__item--selected': index === activeIndex }" :aria-label="'Image ' + index"
            @click="go(index)">
            <SfImage class="sf-gallery__thumb" :src="image.mobile.url" :alt="image.alt" :placeholder="image.placeholder"
              :width="thumbWidth" :height="thumbHeight" :image-tag="thumbImageTag"
              :nuxt-img-config="thumbNuxtImgConfig" />
          </SfButton>
        </slot>
      </div>
    </div>
  </div>
</template>
<script>

import Glide from "@glidejs/glide";

import {
  SfButton,
  SfImage
} from '@storefront-ui/vue';
export default {
  name: "Gallery",
  components: {
    SfImage,
    SfButton
  },
  props: {
    images: {
      type: Array,
      default: () => []
    },
    imageWidth: {
      type: [Number, String],
      default: 560
    },
    imageHeight: {
      type: [Number, String],
      default: 560
    },
    thumbWidth: {
      type: [Number, String],
      default: null
    },
    thumbHeight: {
      type: [Number, String],
      default: null
    },
    current: {
      type: Number,
      default: 1
    },
    sliderOptions: {
      type: Object,
      default() {
        return {
          type: "slider",
          autoplay: false,
          rewind: false,
          gap: 0
        };
      }
    },
    outsideZoom: {
      type: Boolean,
      default: false
    },
    enableZoom: {
      type: Boolean,
      default: false
    },
    imageTag: {
      type: String,
      default: "img"
    },
    nuxtImgConfig: {
      type: Object,
      default: () => ({})
    },
    thumbImageTag: {
      type: String,
      default: "img"
    },
    thumbNuxtImgConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      positionStatic: {},
      eventHover: {},
      glide: null,
      activeIndex: this.current - 1,
      style: "",
      pictureSelected: this.images[0] || {
        alt: "",
        zoom: "",
        big: {
          url: ""
        },
        desktop: "",
        placeholder: ""
      },
      isZoomStarted: false
    };
  },
  computed: {
    mapPictures() {
      // map images to handle picture tags with SfImage
      return this.images.map(({ desktop, big }) => ({
        mobile: desktop,
        desktop: big
      }));
    },
    updatedSliderOptions() {
      return { ...this.sliderOptions, startAt: this.activeIndex };
    },
    definedPicture() {
      const { zoom, big, desktop } = this.pictureSelected;
      const definedPicture = zoom || big || desktop;
      definedPicture ? (definedPicture.alt = this.pictureSelected?.alt) : null;
      definedPicture
        ? (definedPicture.placeholder = this.pictureSelected?.placeholder)
        : null;
      return definedPicture ? definedPicture : "";
    }
  },
  mounted() {
    this.$nextTick(() => {
      // handle slider with swipe and transitions with Glide.js
      // https://glidejs.com/docs/
      if (this.images.length < 1) return;
      this.$refs.Gallery.style.setProperty(
        "--active-image-width",
        `${this.imageWidth}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--active-image-height",
        `${this.imageHeight}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--gallery-stage-width",
        `${this.imageWidth}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--gallery-thumb-width",
        `${this.thumbWidth}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--gallery-thumbs-image-height",
        `${this.thumbHeight}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--gallery-thumbs-image-width",
        `${this.thumbWidth}px`
      );
      this.$refs.Gallery.style.setProperty(
        "--gallery-thumb-height",
        `${this.thumbHeight}px`
      );
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on("run", () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
    });
  },
  updated() {
    if (this.glide) {
      this.$nextTick(() => {
        this.glide.mount();
      });
    }
  },
  beforeDestroy() {
    if (this.glide) {
      this.glide.destroy();
    }
  },
  methods: {
    positionObject(index) {
      if (this.$refs.sfGalleryBigImage) {
        if (this.outsideZoom) {
          return this.$refs.glide.getBoundingClientRect();
        } else {
          return this.$refs.sfGalleryBigImage[
            index
          ].$el.getBoundingClientRect();
        }
      }
      return "";
    },
    go(index) {
      this.pictureSelected = this.images[index];
      if (!this.glide) return;
      this.activeIndex = index;
      this.$emit("click", index + 1);
      if (this.glide) {
        this.glide.go(`=${index}`);
      }
    },
    startZoom() {
      if (this.enableZoom) {
        this.isZoomStarted = true;
      }
    },
    moveZoom($event, index) {
      if (this.enableZoom) {
        this.eventHover = $event;
        if (this.outsideZoom) {
          this.positionStatic = this.positionObject(index);
          this.$refs.outSide.style.cssText = `position: absolute; left: ${$event.clientX - this.positionStatic.x
          }px; top: ${$event.clientY - this.positionStatic.y}px; z-index: 1;`;
          this.$refs.imgZoom.$el.children[0].style.cssText = `transform: scale(2); width: 300px; height: auto;`;
          this.$refs.imgZoom.$el.children[0].style.transformOrigin = `${$event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        } else {
          this.positionStatic = this.positionObject(index);
          this.$refs.sfGalleryBigImage[index].$el.children[0].style.cssText =
            "top: 0; transform: scale(2);";
          this.$refs.sfGalleryBigImage[
            index
          ].$el.children[0].style.transformOrigin = `${$event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        }
      }
    },
    removeZoom(index) {
      if (this.enableZoom) {
        this.isZoomStarted = false;
        if (this.outsideZoom) return;
        this.$refs.sfGalleryBigImage[index].$el.children[0].style.transform =
          "scale(1)";
      }
    }
  }
};
</script>
<style lang="scss">

.Gallery{
  // --gallery-stage-width: 560px;
  // .sf-gallery__thumbs{
  //   --gallery-thumbs-flex: 0 0 var(--gallery-thumb-width, 120px);
  //   --gallery-thumbs-image-height: 120px;
  //   --gallery-thumbs-image-width: 120px;
  //   .sf-gallery__item{
  //     --gallery-thumb-width: 120px;
  //   }
  // }
    .sf-gallery__stage{
      max-height: var(--active-image-height);
    }
    .gallery__thumbsControll{
      @include for-mobile {
        padding: 0 var(--spacer-sm) !important;
      }
      @include for-desktop{
        order: var(--gallery-thumbs-order);
      }
    }
    .sf-gallery__thumbs {
      @include for-desktop {
        height: var(--active-image-width);
      }
    }
    .sf-gallery__big-image {
      @include for-desktop {
        --_image-height: var(--active-image-height) !important;
        --_image-width: var(--active-image-width) !important;
        --image-width: var(--active-image-width) !important;
        --image-height: var(--active-image-height) !important;
      }
      @include for-mobile {
        --_image-width: 100vw;
        --_image-width: 100vw;
        --image-width: 100vw;
        --image-height: 100vw;
        width: 100vw;
        height: 100vw;
        max-height: 100vw;
      }
      .sf-image-loaded {
        object-fit: contain;
        object-position: top;
      }
    }
    .glide__slide--active {
      z-index: 9999999999999;
      width: var(--active-image-width) !important;
      position: relative;
      background: #fff;
    }
    .sf-gallery__item--selected{
      border: 2px solid var(--c-primary);
      border-radius:4px;
      .sf-image{
        width: calc(var(--gallery-thumb-width) - 4px);
        height: calc(var(--gallery-thumb-height) - 4px);
        // border: 1px solid var(--c-primary);
      }
    }
}

@import "~@storefront-ui/shared/styles/components/molecules/SfGallery.scss";
</style>
